import { useId, type ReactNode } from 'react';

import { copy } from '@shared/copy';

import { cn } from '../cn';
import { Dialog } from './Dialog';

/**
 * The console's load-bearing UX primitive. Every destructive and every
 * value-moving action passes through it, and the copy states the effect in
 * Arabic **before** it fires.
 *
 * Three rules travel with every destructive control and each fails without the
 * other two:
 *
 * - **Quiet in rest** — the trigger is a ghost control that takes the danger
 *   colour only on hover and focus. Nine permanently red buttons make a list
 *   read as a continuous alarm, and an alarm that is always on is not read.
 * - **Loud in confirmation** — the dialog names the thing and what goes with
 *   it. A generic «هل أنت متأكّد؟» cannot state a decision-specific
 *   consequence, which is the whole point of the step.
 * - **Visible in refusal** — where the action is not permitted the control
 *   stays and this dialog opens with `refusal`, which removes the commit button
 *   and leaves the way out in the body. Hiding the control cannot distinguish
 *   "not allowed" from "not shown here".
 *
 * **One dialog per table, not one per row**: the heading, body and commit label
 * are written from the trigger's `data-confirm-*` at open time, so the words
 * stay in the markup where a reviewer checks them and the dialog stays one
 * element.
 *
 * `catalogue_delete` is **hard** and `editorial_delete` is **soft**, and this
 * console puts one a row away from the other — the body must say which it is.
 */

export type ConfirmIntent = 'danger' | 'warning' | 'commit';

export interface ConfirmSubject {
  heading: string;
  body: string;
  commitLabel: string;
  intent: ConfirmIntent;
  /** True when the action is refused: no commit button, the way out in `body`. */
  refusal?: boolean;
}

const COMMIT_CLASSES: Record<ConfirmIntent, string> = {
  // A destructive control is never filled: `--danger` and `--accent-text` sit
  // 6° apart, so a filled red button is indistinguishable from «play». The
  // distinction is form, not hue.
  danger: 'border border-danger text-danger hover:bg-danger-soft',
  warning: 'border border-warning text-warning hover:bg-warning-soft',
  commit: 'bg-accent text-accent-ink hover:bg-accent-hover',
};

export function ConfirmDialog({
  open,
  subject,
  busy = false,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  subject: ConfirmSubject | null;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Extra controls the decision needs — a required reason, a date field. */
  children?: ReactNode;
}) {
  const headingId = useId();

  if (!subject) return null;

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      onClose={(returnValue) => {
        if (returnValue === 'confirm') onConfirm();
        else onCancel();
      }}
    >
      <form method="dialog" className="flex flex-col gap-4">
        <h2 id={headingId} className="text-h3">
          {subject.heading}
        </h2>

        <p className="text-sm text-ink-muted">{subject.body}</p>

        {children}

        <div className="flex flex-wrap gap-2">
          {/*
           * Both buttons in a `method="dialog"` form are submits — that is what
           * carries the choice back in `returnValue` — and a submit runs
           * constraint validation. Without `formNoValidate` the browser blocks
           * the **exit** and demands a value from an operator who has just
           * decided not to supply one, turning the dialog into a trap whose
           * only signposted exits are `Esc` and the backdrop.
           */}
          {/*
           * `jsx-a11y/no-autofocus` guards against a page that steals focus on
           * load. This is a modal: `showModal()` moves focus into the dialog
           * whatever happens, so the only question is *which* control receives
           * it — and on a confirmation the answer is the safe one. Focus lands
           * on the way out, never on the commit, so a stray Enter cancels
           * rather than destroys.
           */}
          <button
            type="submit"
            value="cancel"
            formNoValidate
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {copy.action.cancel}
          </button>

          {subject.refusal ? null : (
            <button
              type="submit"
              value="confirm"
              disabled={busy}
              className={cn(
                'rounded-sm px-5 py-3 text-sm disabled:cursor-not-allowed disabled:opacity-45',
                COMMIT_CLASSES[subject.intent],
              )}
            >
              {subject.commitLabel}
            </button>
          )}
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Reads one table's confirmation copy off the row that triggered it. The
 * trigger is expected to close its own row menu on the way, so the menu is not
 * left open behind the backdrop.
 */
export function readConfirmSubject(trigger: HTMLElement): ConfirmSubject | null {
  const { confirmHeading, confirmBody, confirmCommit, confirmIntent, confirmRefusal } = trigger.dataset;

  if (!confirmHeading || !confirmBody || !confirmCommit) return null;

  return {
    heading: confirmHeading,
    body: confirmBody,
    commitLabel: confirmCommit,
    intent: (confirmIntent as ConfirmIntent | undefined) ?? 'warning',
    refusal: confirmRefusal === 'true',
  };
}
